// Arc Gauge Component rendering a vibrant rounded arc progress chart for the overall average %

export class ArcGauge {
  constructor(canvasElement, percentageElement, badgeElement) {
    this.canvas = canvasElement;
    this.percentageEl = percentageElement;
    this.badgeEl = badgeElement;
    this.ctx = this.canvas.getContext('2d');
    
    // Internal animation state
    this.currentVal = 0;
    this.targetVal = 0;
    this.currentMood = null;
    this.animFrame = null;

    // Retina / High-DPI Canvas Scaling
    this.setupDPI();
    window.addEventListener('resize', () => this.setupDPI());
    this.startLoop();
  }

  setupDPI() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 2;
    this.width = (rect && rect.width > 0) ? rect.width : 280;
    this.height = (rect && rect.height > 0) ? rect.height : 120;

    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    if (this.ctx.resetTransform) {
      this.ctx.resetTransform();
    } else {
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    this.ctx.scale(dpr, dpr);
  }

  update(state) {
    if (!state || !state.mood) return;
    this.targetVal = state.averagePercentage || 0;
    this.currentMood = state.mood;

    // Update textual percentage
    if (this.percentageEl) {
      this.percentageEl.textContent = `${this.targetVal}%`;
      this.percentageEl.style.color = state.mood.themeColor;
    }

    // Update Mood Badge
    if (this.badgeEl) {
      this.badgeEl.innerHTML = `<span>${state.mood.emoji}</span><span>${state.mood.title}</span>`;
      this.badgeEl.style.backgroundColor = state.mood.badgeBg;
      this.badgeEl.style.color = state.mood.themeColor;
      this.badgeEl.style.borderColor = state.mood.themeColor;
    }
  }

  startLoop() {
    const animate = () => {
      // Smooth interpolation (lerp)
      this.currentVal += (this.targetVal - this.currentVal) * 0.1;
      this.drawArc();
      this.animFrame = requestAnimationFrame(animate);
    };
    animate();
  }

  drawArc() {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    ctx.clearRect(0, 0, w, h);

    const centerX = w / 2;
    const centerY = h - 14;
    const radius = Math.min(centerX - 24, h - 20);
    const lineWidth = 12;

    const startAngle = Math.PI * 0.85;
    const endAngle = Math.PI * 2.15;
    const totalAngle = endAngle - startAngle;

    // 1. Draw Background Track (Muted Dark Arc)
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.stroke();

    // 2. Draw Subtle Tick Marks along the arc
    const numTicks = 9;
    for (let i = 0; i <= numTicks; i++) {
      const angle = startAngle + (totalAngle * (i / numTicks));
      const tickInner = radius - 10;
      const tickOuter = radius - 15;
      const x1 = centerX + Math.cos(angle) * tickInner;
      const y1 = centerY + Math.sin(angle) * tickInner;
      const x2 = centerX + Math.cos(angle) * tickOuter;
      const y2 = centerY + Math.sin(angle) * tickOuter;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = (i / numTicks) <= (this.currentVal / 100) ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)';
      ctx.stroke();
    }

    // 3. Draw Active Progress Arc
    const progressFrac = Math.max(0, Math.min(1, this.currentVal / 100));
    if (progressFrac > 0.01) {
      const currentAngle = startAngle + totalAngle * progressFrac;

      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, startAngle, currentAngle, false);
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';

      // Dynamic Gradient based on percentage: Green when good, transitioning towards Red when bad
      const grad = ctx.createLinearGradient(centerX - radius, centerY, centerX + radius, centerY);
      if (this.currentVal >= 80) {
        grad.addColorStop(0, '#00C853');
        grad.addColorStop(1, '#00E676');
        ctx.shadowColor = 'rgba(0, 230, 118, 0.6)';
      } else if (this.currentVal >= 50) {
        grad.addColorStop(0, '#76FF03');
        grad.addColorStop(1, '#AEEA00');
        ctx.shadowColor = 'rgba(174, 234, 0, 0.5)';
      } else if (this.currentVal >= 20) {
        grad.addColorStop(0, '#FF6D00');
        grad.addColorStop(1, '#FF9100');
        ctx.shadowColor = 'rgba(255, 145, 0, 0.5)';
      } else {
        grad.addColorStop(0, '#D50000');
        grad.addColorStop(1, '#FF1744');
        ctx.shadowColor = 'rgba(255, 23, 68, 0.7)';
      }

      ctx.shadowBlur = 12;
      ctx.strokeStyle = grad;
      ctx.stroke();
      ctx.restore();

      // 4. Draw Leading Glowing Tip Dot
      const tipX = centerX + Math.cos(currentAngle) * radius;
      const tipY = centerY + Math.sin(currentAngle) * radius;

      ctx.save();
      ctx.beginPath();
      ctx.arc(tipX, tipY, lineWidth / 2 + 1, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.shadowColor = '#FFFFFF';
      ctx.shadowBlur = 8;
      ctx.fill();
      ctx.restore();
    }
  }

  destroy() {
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
    }
  }
}
